import { Link } from "react-router-dom";
import { useApi } from "../../lib/useApi";
import { Card, PageHeader, Spinner, EmptyState } from "../../components/ui";
import { StatusBadge } from "../../components/StatusBadge";
import { date } from "../../lib/format";
import type { CaseStatus } from "../../lib/types";

interface Dashboard {
  totalCases: number;
  byStatus: Partial<Record<CaseStatus, number>>;
  recentCases: { id: string; publicRef: string; status: CaseStatus; createdAt: number }[];
}

export function PortalDashboard() {
  const { data, loading } = useApi<Dashboard>("/api/portal/dashboard");
  if (loading) return <Spinner />;
  if (!data) return <EmptyState>No hay datos.</EmptyState>;

  return (
    <>
      <PageHeader title="Tu panel" subtitle="Resumen de tus expedientes de inspección" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Expedientes" value={data.totalCases} />
        <Stat label="Informes listos" value={data.byStatus.REPORT_READY ?? 0} />
        <Stat label="En inspección" value={data.byStatus.INSPECTION_IN_PROGRESS ?? 0} />
        <Stat label="Pendiente de pago" value={data.byStatus.AWAITING_PAYMENT ?? 0} />
      </div>

      <Card className="mt-6">
        <div className="border-b border-line px-5 py-3.5 text-sm font-semibold">Expedientes recientes</div>
        {data.recentCases.length === 0 ? (
          <div className="p-6 text-sm text-muted">Aún no tienes expedientes.</div>
        ) : (
          <ul className="divide-y divide-line">
            {data.recentCases.map((c) => (
              <li key={c.id}>
                <Link to={`/cases/${c.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-canvas">
                  <span className="font-mono text-sm">{c.publicRef}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-sm text-muted">{date(c.createdAt)}</span>
                    <StatusBadge status={c.status} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tnum">{value}</div>
    </Card>
  );
}
